<template>
	<div class="cdn-upload-page">
		<!-- Page Header -->
		<div class="page-header">
			<h1>CDN - File Upload</h1>
			<p class="subtitle">Upload and manage content files</p>
		</div>

		<!-- Upload Section -->
		<div class="upload-section">
			<div class="upload-header">
				<button class="collapse-btn" @click="isUploadExpanded = !isUploadExpanded">
					<span class="collapse-icon" :class="{ rotated: isUploadExpanded }">›</span>
					<span>Upload Files</span>
				</button>
			</div>

			<transition name="collapse-transition">
				<div v-if="isUploadExpanded" class="upload-content">
					<!-- Upload Area -->
					<div
						class="upload-area"
						@dragover.prevent="isDragging = true"
						@dragleave.prevent="isDragging = false"
						@drop.prevent="onDrop"
						:class="{ dragging: isDragging }"
					>
						<div class="upload-icon">📤</div>
						<h3>Drag & Drop Files Here</h3>
						<p class="upload-subtitle">or</p>
						<button class="action-btn primary" @click="triggerFileInput">
							Select Files
						</button>
						<input
							ref="fileInput"
							type="file"
							multiple
							accept="image/*,video/*,.pdf"
							@change="onFilesSelected"
							class="file-input"
						/>
						<p class="supported-formats">
							Supported: JPG, PNG, GIF, SVG, MP4, PDF
						</p>
					</div>

					<!-- Alerts -->
					<transition name="fade">
						<div v-if="showAlert" class="alert" :class="`alert-${alertVariant}`">
							<span class="alert-message">{{ alertMessage }}</span>
							<button class="alert-close" @click="showAlert = false">✕</button>
						</div>
					</transition>

					<!-- Upload Queue -->
					<div v-if="uploadQueue.length > 0" class="upload-queue">
						<h4>Upload Queue</h4>
						<div
							v-for="(file, index) in uploadQueue"
							:key="index"
							class="upload-item"
							:class="file.status"
						>
							<div class="upload-item-header">
								<div class="file-info">
									<div class="file-icon">
										<template v-if="isImageFile(file.name)">
											<img v-if="file.preview" :src="file.preview" :alt="file.name" />
											<span v-else>🖼️</span>
										</template>
										<template v-else>
											📄
										</template>
									</div>
									<div class="file-details">
										<div class="file-name">{{ file.name }}</div>
										<div class="file-size">{{ formatFileSize(file.size) }}</div>
									</div>
								</div>
								<div class="upload-status">
									<template v-if="file.status === 'uploading'">
										<div class="spinner"></div>
									</template>
									<template v-else-if="file.status === 'success'">
										<span class="status-icon success">✓</span>
									</template>
									<template v-else-if="file.status === 'error'">
										<span class="status-icon error">✕</span>
										<button class="retry-btn" @click="retryUpload(file)">Retry</button>
									</template>
								</div>
							</div>
							<div v-if="file.status === 'uploading'" class="progress-bar">
								<div class="progress" :style="{ width: file.progress + '%' }"></div>
								<span class="progress-text">{{ file.progress }}%</span>
							</div>
						</div>
					</div>

					<!-- Actions -->
					<div class="upload-actions">
						<button
							class="action-btn secondary"
							@click="refreshFileList"
							:disabled="isUploading"
						>
							🔄 Refresh List
						</button>
					</div>
				</div>
			</transition>
		</div>

		<!-- File List Section -->
		<div class="file-list-section">
			<div class="search-box">
				<input
					v-model="searchQuery"
					type="text"
					placeholder="Search files..."
					class="search-input"
				/>
			</div>

			<div v-if="loading" class="loading">
				<div class="spinner"></div>
				<p>Loading files...</p>
			</div>

			<div v-else-if="filteredFiles && filteredFiles.length > 0" class="file-grid">
				<div v-for="file in filteredFiles" :key="file.fileName" class="file-card">
					<div class="file-preview">
						<template v-if="isImageFile(file.fileName)">
							<img :src="file.uri" :alt="file.fileName" @error="handleImageError" />
						</template>
						<template v-else-if="isVideoFile(file.fileName)">
							<video :src="file.uri" muted preload="metadata" />
						</template>
						<template v-else>
							<div class="file-icon-large">📄</div>
						</template>
					</div>
					<div class="file-card-info">
						<div class="file-card-name" :title="file.fileName">
							{{ file.fileName }}
						</div>
						<div class="file-card-date">
							{{ formatDate(file.lastModifiedDateTime) }}
						</div>
						<div class="file-card-actions">
							<a
								:href="file.uri"
								target="_blank"
								class="action-link view-link"
								title="Open file"
							>
								🔗
							</a>
							<button
								class="action-link delete-link"
								@click="deleteFile(file)"
								title="Delete file"
							>
								🗑️
							</button>
						</div>
					</div>
				</div>
			</div>

			<div v-else class="empty-state">
				No files found
			</div>
		</div>

		<!-- Delete Confirmation Dialog -->
		<div v-if="fileToDelete" class="modal-overlay" @click="cancelDelete">
			<div class="modal-dialog" @click.stop>
				<div class="modal-header">
					<h3>Delete File</h3>
					<button class="close-btn" @click="cancelDelete">✕</button>
				</div>
				<div class="modal-body">
					<p>Are you sure you want to delete <strong>{{ fileToDelete.fileName }}</strong>?</p>
					<p class="warning-text">⚠️ This action cannot be undone.</p>
				</div>
				<div class="modal-actions">
					<button class="action-btn secondary" @click="cancelDelete">Cancel</button>
					<button class="action-btn danger" @click="confirmDelete">Delete</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import AdminServices from '@/admin/mixins/AdminServices';

export default {
	name: 'CDNUploadPage',
	mixins: [AdminServices],
	data() {
		return {
			files: [],
			uploadQueue: [],
			searchQuery: '',
			isUploadExpanded: false,
			isDragging: false,
			isUploading: false,
			loading: false,
			showAlert: false,
			alertMessage: '',
			alertVariant: 'success',
			fileToDelete: null,
			supportedTypes: ['.jpg', '.jpeg', '.png', '.gif', '.svg', '.mp4', '.pdf']
		};
	},
	computed: {
		filteredFiles() {
			if (!this.files) return [];
			const query = this.searchQuery.toLowerCase();
			return this.files.filter(f =>
				f.fileName.toLowerCase().includes(query) &&
				this.supportedTypes.some(type => f.fileName.toLowerCase().endsWith(type))
			);
		}
	},
	methods: {
		triggerFileInput() {
			this.$refs.fileInput.click();
		},
		onFilesSelected(event) {
			const files = Array.from(event.target.files);
			this.addFilesToQueue(files);
			event.target.value = '';
		},
		onDrop(event) {
			this.isDragging = false;
			const files = Array.from(event.dataTransfer.files);
			this.addFilesToQueue(files);
		},
		generateGuid() {
			return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
				const r = (Math.random() * 16) | 0;
				const v = c === 'x' ? r : (r & 0x3) | 0x8;
				return v.toString(16);
			});
		},
		addFilesToQueue(files) {
			files.forEach(file => {
				const fileType = '.' + file.name.split('.').pop().toLowerCase();
				if (!this.supportedTypes.includes(fileType)) {
					this.showMessage(`File type ${fileType} is not supported`, 'error');
					return;
				}

				let preview = null;
				if (file.type.startsWith('image/')) {
					preview = URL.createObjectURL(file);
				}

				const CHUNK_SIZE = 1024 * 1024; // 1MB
				const chunkCount = Math.ceil(file.size / CHUNK_SIZE);

				this.uploadQueue.push({
					file,
					name: file.name,
					size: file.size,
					preview,
					status: 'pending',
					progress: 0,
					chunkCount,
					currentChunk: 0,
					uploadId: this.generateGuid()
				});
			});

			if (!this.isUploading) {
				this.processUploadQueue();
			}
		},
		async processUploadQueue() {
			this.isUploading = true;
			for (const item of this.uploadQueue) {
				if (item.status === 'pending' || item.status === 'error') {
					item.status = 'uploading';
					item.progress = 0;
					item.currentChunk = 0;
					try {
						await this.uploadFileInChunks(item);
						item.status = 'success';
						item.progress = 100;
					} catch (error) {
						console.error('Upload error:', error);
						item.status = 'error';
						this.showMessage(`Failed to upload ${item.name}`, 'error');
					}
				}
			}
			setTimeout(() => {
				this.uploadQueue = this.uploadQueue.filter(item => item.status !== 'success');
				this.isUploading = false;
				if (this.uploadQueue.some(item => item.status === 'pending')) {
					this.processUploadQueue();
				}
			}, 2000);
			this.refreshFileList();
		},
		async uploadFileInChunks(item) {
			const CHUNK_SIZE = 1024 * 1024; // 1MB
			const totalChunks = item.chunkCount;
			for (let i = 0; i < totalChunks; i++) {
				const start = i * CHUNK_SIZE;
				const end = Math.min(item.size, start + CHUNK_SIZE);
				const chunk = item.file.slice(start, end);
				const base64 = await this.getBase64(chunk);

				await new Promise((resolve, reject) => {
					this._uploadFileChunk(
						base64,
						item.name,
						i,
						totalChunks,
						item.uploadId,
						() => resolve(),
						(error) => reject(new Error(error))
					);
				});

				item.currentChunk = i;
				item.progress = Math.round(((i + 1) / totalChunks) * 100);
			}
		},
		getBase64(file) {
			return new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = (e) => resolve(e.target.result.split(',')[1]);
				reader.onerror = reject;
				reader.readAsDataURL(file);
			});
		},
		retryUpload(item) {
			item.status = 'pending';
			if (!this.isUploading) {
				this.processUploadQueue();
			}
		},
		refreshFileList() {
			this.loading = true;
			this._getFiles(
				(filesData) => {
					this.files = filesData || [];
					this.loading = false;
				},
				(error) => {
					this.showMessage(error, 'error');
					this.loading = false;
				}
			);
		},
		deleteFile(file) {
			this.fileToDelete = file;
		},
		cancelDelete() {
			this.fileToDelete = null;
		},
		confirmDelete() {
			if (!this.fileToDelete) return;
			this._deleteFile(
				this.fileToDelete.fileName,
				() => {
					this.showMessage(`File "${this.fileToDelete.fileName}" deleted successfully`);
					this.fileToDelete = null;
					this.refreshFileList();
				},
				(error) => {
					this.showMessage(error, 'error');
					this.fileToDelete = null;
				}
			);
		},
		isImageFile(fileName) {
			const ext = fileName.toLowerCase().split('.').pop();
			return ['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(ext);
		},
		isVideoFile(fileName) {
			return fileName.toLowerCase().endsWith('.mp4');
		},
		formatFileSize(bytes) {
			if (bytes === 0) return '0 Bytes';
			const k = 1024;
			const sizes = ['Bytes', 'KB', 'MB', 'GB'];
			const i = Math.floor(Math.log(bytes) / Math.log(k));
			return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
		},
		formatDate(dateString) {
			if (!dateString) return '-';
			const date = new Date(dateString);
			return date.toLocaleString('en-GB');
		},
		handleImageError(event) {
			event.target.style.display = 'none';
		},
		showMessage(message, variant = 'success') {
			this.alertMessage = message;
			this.alertVariant = variant;
			this.showAlert = true;
			if (variant !== 'error') {
				setTimeout(() => {
					this.showAlert = false;
				}, 5000);
			}
		}
	},
	mounted() {
		this.refreshFileList();
	},
	beforeUnmount() {
		this.uploadQueue.forEach(item => {
			if (item.preview) {
				URL.revokeObjectURL(item.preview);
			}
		});
	}
};
</script>

<style scoped>
.cdn-upload-page {
	min-height: 100%;
	padding: 20px;
}

/* Upload Section */
.upload-section {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	margin-bottom: 20px;
	box-shadow: var(--box-shadow);
}

.upload-header {
	padding: 15px 20px;
	border-bottom: 1px solid var(--admin-border);
	display: flex;
	align-items: center;
}

.collapse-btn {
	display: flex;
	align-items: center;
	gap: 10px;
	background: none;
	border: none;
	color: var(--admin-text-primary);
	font-size: 16px;
	font-weight: 600;
	cursor: pointer;
	padding: 0;
	transition: all 0.2s ease;
}

.collapse-btn:hover {
	color: var(--background-color-primary);
}

.collapse-icon {
	display: inline-block;
	transition: transform 0.3s ease;
	font-size: 20px;
}

.collapse-icon.rotated {
	transform: rotate(90deg);
}

.upload-content {
	padding: 20px;
}

.upload-area {
	border: 2px dashed var(--admin-border);
	border-radius: var(--border-radius-s);
	padding: 40px 20px;
	text-align: center;
	transition: all 0.3s ease;
	background-color: var(--admin-hover-bg);
}

.upload-area.dragging {
	border-color: var(--background-color-primary);
	background-color: rgba(51, 40, 71, 0.1);
}

.upload-icon {
	font-size: 48px;
	margin-bottom: 15px;
}

.upload-area h3 {
	margin: 0 0 10px 0;
	color: var(--admin-text-primary);
	font-size: 18px;
	font-weight: 600;
}

.upload-subtitle {
	color: var(--admin-text-secondary);
	margin: 0 0 15px 0;
	font-size: 14px;
}

.file-input {
	display: none;
}

.supported-formats {
	color: var(--admin-text-secondary);
	font-size: 12px;
	margin: 15px 0 0 0;
}

/* Alerts */
.alert {
	margin: 15px 0;
	padding: 12px 15px;
	border-radius: var(--border-radius-s);
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.alert-success {
	background-color: rgba(76, 175, 80, 0.1);
	color: #2e7d32;
	border: 1px solid rgba(76, 175, 80, 0.3);
}

.alert-error {
	background-color: rgba(211, 47, 47, 0.1);
	color: #c62828;
	border: 1px solid rgba(211, 47, 47, 0.3);
}

.alert-close {
	background: none;
	border: none;
	color: inherit;
	cursor: pointer;
	font-size: 16px;
	padding: 0;
	width: 24px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
}

/* Upload Queue */
.upload-queue {
	margin: 20px 0;
	padding: 15px;
	background-color: var(--admin-hover-bg);
	border-radius: var(--border-radius-s);
}

.upload-queue h4 {
	margin: 0 0 15px 0;
	font-size: 14px;
	color: var(--admin-text-primary);
	font-weight: 600;
}

.upload-item {
	padding: 12px;
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	margin-bottom: 10px;
	background-color: var(--admin-bg-secondary);
	transition: all 0.2s ease;
}

.upload-item.success {
	border-color: #4caf50;
	background-color: rgba(76, 175, 80, 0.05);
}

.upload-item.error {
	border-color: #d32f2f;
	background-color: rgba(211, 47, 47, 0.05);
}

.upload-item-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 8px;
}

.file-info {
	display: flex;
	align-items: center;
	gap: 10px;
	flex: 1;
}

.file-icon {
	width: 40px;
	height: 40px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: var(--admin-hover-bg);
	border-radius: var(--border-radius-s);
	font-size: 20px;
	flex-shrink: 0;
}

.file-icon img {
	width: 100%;
	height: 100%;
	object-fit: cover;
	border-radius: var(--border-radius-s);
}

.file-details {
	min-width: 0;
}

.file-name {
	font-weight: 500;
	color: var(--admin-text-primary);
	font-size: 13px;
	word-break: break-word;
	margin-bottom: 3px;
}

.file-size {
	font-size: 12px;
	color: var(--admin-text-secondary);
}

.upload-status {
	display: flex;
	align-items: center;
	gap: 10px;
	flex-shrink: 0;
}


.status-icon {
	font-weight: bold;
	font-size: 16px;
}

.status-icon.success {
	color: #4caf50;
}

.status-icon.error {
	color: #d32f2f;
}

.retry-btn {
	padding: 4px 8px;
	font-size: 12px;
	background-color: #d32f2f;
	color: white;
	border: none;
	border-radius: var(--border-radius-s);
	cursor: pointer;
	transition: all 0.2s ease;
}

.retry-btn:hover {
	background-color: #b71c1c;
}

.progress-bar {
	position: relative;
	height: 4px;
	background-color: var(--admin-border);
	border-radius: 2px;
	overflow: hidden;
	margin-top: 8px;
}

.progress {
	height: 100%;
	background-color: var(--background-color-primary);
	transition: width 0.2s ease;
}

.progress-text {
	position: absolute;
	right: 8px;
	top: -18px;
	font-size: 11px;
	color: var(--admin-text-secondary);
}

/* Upload Actions */
.upload-actions {
	margin-top: 15px;
	display: flex;
	gap: 10px;
}

/* File List Section */
.file-list-section {
	background-color: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	padding: 20px;
	box-shadow: var(--box-shadow);
}

.file-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
	gap: 15px;
}

.file-card {
	background-color: var(--admin-hover-bg);
	border-radius: var(--border-radius-s);
	border: 1px solid var(--admin-border);
	overflow: hidden;
	transition: all 0.2s ease;
}

.file-card:hover {
	transform: translateY(-2px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.file-preview {
	width: 100%;
	height: 120px;
	background-color: var(--admin-bg-secondary);
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
}

.file-preview img,
.file-preview video {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.file-icon-large {
	font-size: 32px;
}

.file-card-info {
	padding: 12px;
}

.file-card-name {
	font-weight: 500;
	color: var(--admin-text-primary);
	font-size: 13px;
	margin-bottom: 6px;
	word-break: break-word;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.file-card-date {
	font-size: 11px;
	color: var(--admin-text-secondary);
	margin-bottom: 10px;
}

.file-card-actions {
	display: flex;
	gap: 8px;
}

.action-link {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	border-radius: var(--border-radius-s);
	cursor: pointer;
	transition: all 0.2s ease;
	font-size: 14px;
	text-decoration: none;
}

.view-link {
	background-color: var(--admin-bg-secondary);
	border: 1px solid var(--admin-border);
	color: var(--admin-text-primary);
}

.view-link:hover {
	background-color: var(--background-color-primary);
	color: white;
}

.delete-link {
	background-color: var(--admin-bg-secondary);
	border: 1px solid var(--admin-border);
	color: var(--admin-text-primary);
	padding: 0;
	font-size: 14px;
}

.delete-link:hover {
	background-color: #d32f2f;
	color: white;
}

.warning-text {
	color: #d32f2f !important;
	font-weight: 500;
}

/* Responsive */
@media (max-width: 768px) {
	.file-grid {
		grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
	}

	.upload-area {
		padding: 30px 15px;
	}

	.upload-area h3 {
		font-size: 16px;
	}

	.upload-icon {
		font-size: 36px;
	}
}
</style>
